import { ApyFilterEnum } from "~/types/apy";
import { EndsInFilterEnum } from "~/types/endsIn";
import type { IEventFilters } from "~/types/eventFilters";
import { MetricsScopeEnum } from "~/types/metrics";
import type { IOption } from "~/types/option";
import { RoiFilterEnum } from "~/types/roi";
import { endsInFilterKeys } from "~/utils/filters/endsInFilter";

export enum ActivityFilterType {
  All = "all",
  Active = "active",
  Resolved = "resolved",
}

export enum StatusAwareFilter {
  Probability = "probability",
  Spread = "spread",
  EndsIn = "endsIn",
  Apy = "apy",
  Roi = "roi",
  Liquidity = "liquidity",
}

export const FILTER_MODE_TOOLTIP =
  "Choose how filters are applied: to the entire event (all markets combined) or to individual markets / outcomes.";

export const RESOLVED_FILTER_TOOLTIP =
  "This combination of filters can filter only active events and markets";

export const requestParamsByActivityFilterType: Record<
  ActivityFilterType,
  Pick<IEventFilters, "active" | "resolved">
> = {
  [ActivityFilterType.All]: { active: undefined, resolved: undefined },
  [ActivityFilterType.Active]: { active: true, resolved: undefined },
  [ActivityFilterType.Resolved]: { active: undefined, resolved: true },
};

export const labelByActivityFilterType: Record<ActivityFilterType, string> = {
  [ActivityFilterType.All]: "All",
  [ActivityFilterType.Active]: "Active",
  [ActivityFilterType.Resolved]: "Resolved",
};

export const labelByMetricsScope: Record<MetricsScopeEnum, string> = {
  [MetricsScopeEnum.Event]: "Events",
  [MetricsScopeEnum.Market]: "Markets",
};

export const metricsScopeOptions: IOption<MetricsScopeEnum>[] = [
  {
    label: labelByMetricsScope[MetricsScopeEnum.Event],
    value: MetricsScopeEnum.Event,
  },
  {
    label: labelByMetricsScope[MetricsScopeEnum.Market],
    value: MetricsScopeEnum.Market,
  },
];

const activeOnlyFilters: {
  name: StatusAwareFilter;
  keys: (keyof IEventFilters)[];
}[] = [
  { name: StatusAwareFilter.Probability, keys: ["minChance", "maxChance"] },
  { name: StatusAwareFilter.Spread, keys: ["minSpread"] },
  { name: StatusAwareFilter.EndsIn, keys: [...endsInFilterKeys] },
  { name: StatusAwareFilter.Apy, keys: ["apyFilter"] },
  { name: StatusAwareFilter.Roi, keys: ["roiFilter"] },
  { name: StatusAwareFilter.Liquidity, keys: ["minLiquidity", "maxLiquidity"] },
];

const valueThatMeansAll: Partial<Record<keyof IEventFilters, unknown>> = {
  endsIn: EndsInFilterEnum.ALL,
  apyFilter: ApyFilterEnum.ALL,
  roiFilter: RoiFilterEnum.ALL,
};

const isFilterApplied = (
  key: keyof IEventFilters,
  filters: Partial<IEventFilters>
) => {
  const value = filters[key];
  return value !== undefined && value !== null && value !== valueThatMeansAll[key];
};

export const filtersToClearForResolvedEvents: Partial<IEventFilters> =
  Object.fromEntries(
    activeOnlyFilters
      .flatMap((filter) => filter.keys)
      .map((key) => [key, undefined])
  );

export const hasFiltersThatNeedActiveEvents = (filters: Partial<IEventFilters>) =>
  activeOnlyFilters.some((filter) =>
    filter.keys.some((key) => isFilterApplied(key, filters))
  );

export const isResolvedActivityFilterSelected = (
  filters: Pick<IEventFilters, "active" | "resolved">
) => filters.resolved === true;

export const getStatusFilterVisibility = (
  filters: Pick<IEventFilters, "active" | "resolved">
): Record<StatusAwareFilter, boolean> => {
  const showActiveOnlyFilters = !isResolvedActivityFilterSelected(filters);

  return Object.fromEntries(
    activeOnlyFilters.map((filter) => [filter.name, showActiveOnlyFilters])
  ) as Record<StatusAwareFilter, boolean>;
};

export const buildStatusOptions = (
  disabledStatus: ActivityFilterType | null
): IOption<ActivityFilterType>[] =>
  Object.values(ActivityFilterType).map((filterType) => ({
    label: labelByActivityFilterType[filterType],
    value: filterType,
    disabled: filterType === disabledStatus,
    tooltip:
      filterType === disabledStatus ? RESOLVED_FILTER_TOOLTIP : undefined,
  }));
